import {getAuthClient} from "@trumpf/ux.utils";

const SCOPE = "notificationmanagement";
const TOKEN_RETRIEVAL_TIMEOUT = 20000;

const authClient = getAuthClient(SCOPE);
let unsubscribeFromAuthClient: (() => void) | null = null;

type SubscriberId = string;
type TokenUpdateCallback = (newToken: string | null) => void;
const subscribers = new Map<SubscriberId, TokenUpdateCallback>();

type SubscribeToTokenUpdatesArgs = {subscriberId: string; onTokenUpdate: (token: string | null) => void; timeout?: number};

//TODO add retry functionality

/** Registers subscriber callback to authClient, resolves after receiving an initial token. */
export const addTokenSubscriber = async ({subscriberId, onTokenUpdate, timeout = TOKEN_RETRIEVAL_TIMEOUT}: SubscribeToTokenUpdatesArgs): Promise<void> => {
	if (subscribers.has(subscriberId)) return;

	subscribers.set(subscriberId, onTokenUpdate);

	// if there are no subscribers (yet), subscribe to the authClient
	if (!unsubscribeFromAuthClient)
		unsubscribeFromAuthClient = authClient.subscribe((newToken: string | null) => subscribers.forEach((callback) => callback(newToken)));

	try {
		const token = await getInitialToken(timeout);
		onTokenUpdate(token);
	} catch (error) {
		removeTokenSubscriber(subscriberId);
		throw Object.assign(error, {
			message: `${(error as Error).message} Dangling subscriber '${subscriberId}' removed.`,
		});
	}
};

/** Unregisters subscriber callback from authClient. */
export const removeTokenSubscriber = (subscriberId: string) => {
	if (!subscribers.has(subscriberId)) return;

	subscribers.delete(subscriberId);

	if (subscribers.size === 0) {
		unsubscribeFromAuthClient();
		unsubscribeFromAuthClient = null;
	}
};

/** Retrieves an initial Token for new subscribers or rejects after timeout */
const getInitialToken = async (timeout: number): Promise<string> => {
	let intervalId: ReturnType<typeof setInterval>;

	const tokenPromise = new Promise<string>((resolve) => {
		intervalId = setInterval(() => {
			const token = authClient.getAccessToken();
			if (token) {
				clearInterval(intervalId);
				resolve(token);
			}
		}, 100);
	});

	const timeoutPromise = new Promise<string>((_, reject) =>
		setTimeout(() => {
			clearInterval(intervalId);
			reject(new Error("Token retrieval timed out."));
		}, timeout),
	);

	return Promise.race([tokenPromise, timeoutPromise]);
};
